import type { TFunction } from 'i18next';

import { GEOFILTER_SET_NAME_MAX_LENGTH, type Targetables, type Targeting } from '@feathr/blackbox';

import type { TGeoFilterModalContext } from '../Locations.useModals';
import type { IGeoFilterSetData } from './GeoFilterModal.hooks';

/**
 * Validates the geofilter set name field.
 *
 * Validation rules:
 * - Required: name cannot be empty or whitespace only
 * - Max length: name cannot exceed GEOFILTER_SET_NAME_MAX_LENGTH characters
 *
 * @param geoFilterSetName - The name to validate
 * @param _context - The modal context ('create' or 'edit') - currently unused but kept for API consistency
 * @param t - i18next translation function
 * @returns Array of error messages (empty if valid)
 */
export function validateGeoFilterSetName(
  geoFilterSetName: string,
  _context: TGeoFilterModalContext,
  t: TFunction,
): string[] {
  const errors: string[] = [];

  if (!geoFilterSetName.trim()) {
    errors.push(t('Enter a name for the geofilter set'));
  }

  if (geoFilterSetName.length > GEOFILTER_SET_NAME_MAX_LENGTH) {
    errors.push(
      t('Geofilter set name must be {{maxLength}} characters or less', {
        maxLength: GEOFILTER_SET_NAME_MAX_LENGTH,
      }),
    );
  }

  return errors;
}

/**
 * Validates the GeoFilter modal form and returns an array of error messages.
 *
 * Validation rules:
 * - In 'create' context, geofilter set name is required
 * - At least one location filter must be added
 * - All filters must be complete (no partial/invalid targetings)
 *
 * @param context - The modal context ('create' or 'edit')
 * @param geoFilterSetName - The current name input value
 * @param filteredTargetings - Array of targeting objects added to the form
 * @param hasInvalidTargetings - Whether any targetings are incomplete
 * @param t - i18next translation function
 * @returns Array of translated error messages (empty if valid)
 *
 * @example
 * const errors = getValidationErrors('create', '', [], false, t);
 * // Returns: ['Please enter a name for the geofilter set', 'Please add at least one location']
 */
export function getValidationErrors(
  context: TGeoFilterModalContext,
  geoFilterSetName: string,
  filteredTargetings: Targeting[],
  hasInvalidTargetings: boolean,
  t: TFunction,
): string[] {
  const errors: string[] = [];

  errors.push(...validateGeoFilterSetName(geoFilterSetName, context, t));

  if (filteredTargetings.length === 0) {
    errors.push(t('Please add at least one location'));
  }
  if (hasInvalidTargetings) {
    errors.push(t('Complete all filters before saving'));
  }

  return errors;
}

/**
 * Returns the localized modal title based on the context.
 *
 * @param context - The modal context ('create' or 'edit')
 * @param t - i18next translation function
 * @returns Translated modal title string
 *
 * @example
 * getModalTitle('create', t) // Returns: 'Create GeoFilter Set'
 * getModalTitle('edit', t)   // Returns: 'Edit GeoFilter Set'
 */
export function getModalTitle(context: TGeoFilterModalContext, t: TFunction): string {
  return t('{{context}} GeoFilter Set', {
    context: context === 'create' ? t('Create') : t('Edit'),
  });
}

/**
 * Returns the localized submit button label based on the context.
 *
 * @param context - The modal context ('create' or 'edit')
 * @param t - i18next translation function
 * @returns Translated button label string
 *
 * @example
 * getSubmitButtonLabel('create', t) // Returns: 'Save'
 * getSubmitButtonLabel('edit', t)   // Returns: 'Apply'
 */
export function getSubmitButtonLabel(context: TGeoFilterModalContext, t: TFunction): string {
  return context === 'create' ? t('Save') : t('Apply');
}

/**
 * Parameters for computing form dirty state.
 */
interface IComputeIsDirtyParams {
  /** Current geofilter set name */
  geoFilterSetName: string;
  /** Current default toggle state */
  isDefault: boolean;
  /** Initial data to compare against */
  initialData?: IGeoFilterSetData;
  /** Current filtered targetings */
  filteredTargetings: Targeting[];
  /** Targetables collection for looking up associated targetables */
  Targetables: Targetables;
}

/**
 * Computes whether the geofilter set form has unsaved changes.
 *
 * Checks for changes in:
 * - Geofilter set name
 * - Default toggle state
 * - Number of targetings (added or removed)
 * - Individual targeting or targetable dirty state
 *
 * @param params - The parameters needed to compute dirty state
 * @returns True if any changes have been made, false otherwise
 *
 * @example
 * const isDirty = computeIsDirty({
 *   geoFilterSetName: 'My Set',
 *   isDefault: false,
 *   initialData: { name: 'My Set', is_default: false, ... },
 *   filteredTargetings,
 *   Targetables,
 * });
 */
export function computeIsDirty({
  geoFilterSetName,
  isDefault,
  initialData,
  filteredTargetings,
  Targetables,
}: IComputeIsDirtyParams): boolean {
  const hasNameChanged = geoFilterSetName !== (initialData?.name ?? '');
  const hasDefaultChanged = isDefault !== (initialData?.is_default ?? false);

  const initialTargetingCount =
    (initialData?.included_geo_filters.length ?? 0) +
    (initialData?.excluded_geo_filters.length ?? 0);
  const hasTargetingCountChanged = filteredTargetings.length !== initialTargetingCount;

  const hasTargetingChanges = filteredTargetings.some((targeting) => {
    if (targeting.isDirty) {
      return true;
    }
    const targetableId = targeting.get('target_data');
    if (targetableId) {
      const targetable = Targetables.get(targetableId);
      if (targetable?.isDirty) {
        return true;
      }
    }
    return false;
  });

  return hasNameChanged || hasDefaultChanged || hasTargetingCountChanged || hasTargetingChanges;
}
